const MAX_AVATAR_PX = 800;

function fileFromBlob(blob, fileName = "avatar.jpg") {
  if (!blob || blob.size === 0) {
    throw new Error("Imagem vazia");
  }
  const type =
    blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  let name = fileName || "avatar.jpg";
  if (!/\.(jpe?g|png|gif|webp)$/i.test(name)) {
    const ext = type.includes("png")
      ? "png"
      : type.includes("webp")
        ? "webp"
        : type.includes("gif")
          ? "gif"
          : "jpg";
    name = `avatar.${ext}`;
  }
  return new File([blob], name, { type, lastModified: Date.now() });
}

/**
 * Baixa uma imagem (URL remota, blob: ou data:) e devolve um File para o modal de crop.
 * URLs http(s) remotas podem falhar por CORS — preferir fetchAvatarViaApi para avatares do S3.
 */
export async function fetchImageAsAvatarFile(src, fileName = "avatar.jpg") {
  if (!src || typeof src !== "string") {
    throw new Error("Fonte de imagem inválida");
  }
  const response = await fetch(src);
  if (!response.ok) {
    throw new Error(`Falha ao baixar imagem (${response.status})`);
  }
  return fileFromBlob(await response.blob(), fileName);
}

/**
 * Obtém o avatar do usuário via API (evita CORS do S3 no browser).
 * @param {import('axios').AxiosInstance} api
 * @param {string|number} userId
 * @param {{ original?: boolean }} [options]
 */
export async function fetchAvatarViaApi(api, userId, options = {}) {
  const response = await api.get(`/usuarios-avatar/${userId}`, {
    responseType: "blob",
    params: options.original ? { original: 1 } : undefined,
  });
  const blob = response.data;
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error("Avatar vazio");
  }
  // Axios pode devolver JSON de erro como blob quando status != 2xx não é lançado
  if (blob.type && blob.type.includes("application/json")) {
    throw new Error("Resposta de erro ao buscar avatar");
  }
  const type =
    blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  const ext = type.includes("png")
    ? "png"
    : type.includes("webp")
      ? "webp"
      : type.includes("gif")
        ? "gif"
        : "jpg";
  const base = options.original ? "avatar_original" : "avatar";
  return fileFromBlob(blob, `${base}.${ext}`);
}

/**
 * Recorta o centro quadrado da imagem, sem distorcer proporções.
 * Redimensiona apenas de forma uniforme se exceder MAX_AVATAR_PX.
 */
export function cropAvatarCenterSquare(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const side = Math.min(w, h);
        const sx = Math.floor((w - side) / 2);
        const sy = Math.floor((h - side) / 2);
        const outSide = Math.min(side, MAX_AVATAR_PX);

        const canvas = document.createElement("canvas");
        canvas.width = outSide;
        canvas.height = outSide;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas não disponível"));
          return;
        }
        ctx.drawImage(img, sx, sy, side, side, 0, 0, outSide, outSide);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Falha ao processar imagem"));
              return;
            }
            const baseName = (file.name || "avatar").replace(/\.[^.]+$/, "");
            resolve(
              new File([blob], `${baseName}.jpg`, {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            );
          },
          "image/jpeg",
          0.92,
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler a imagem"));
    };

    img.src = url;
  });
}

/**
 * Escala base para cobrir o viewport (cover) com zoom = 1.
 */
export function getAvatarCoverScale(naturalWidth, naturalHeight, viewportSize) {
  if (!naturalWidth || !naturalHeight || !viewportSize) return 1;
  return viewportSize / Math.min(naturalWidth, naturalHeight);
}

/**
 * Limites de arraste:
 * - zoom >= 1 (imagem maior que o círculo): mantém o viewport coberto
 * - zoom < 1 (imagem menor): permite posicionar a foto dentro do círculo
 */
export function clampAvatarOffset(offsetX, offsetY, naturalWidth, naturalHeight, viewportSize, zoom) {
  const scale = getAvatarCoverScale(naturalWidth, naturalHeight, viewportSize) * zoom;
  const displayedW = naturalWidth * scale;
  const displayedH = naturalHeight * scale;
  const maxX = Math.abs(displayedW - viewportSize) / 2;
  const maxY = Math.abs(displayedH - viewportSize) / 2;
  return {
    x: Math.min(maxX, Math.max(-maxX, offsetX)),
    y: Math.min(maxY, Math.max(-maxY, offsetY)),
  };
}

/**
 * Recorta o avatar a partir do zoom e offset definidos no modal de edição.
 * Renderiza o que está visível no viewport (inclui zoom < 1 com fundo branco).
 * @param {HTMLImageElement} image
 * @param {{ offsetX: number, offsetY: number, zoom: number, viewportSize: number, fileName?: string }} options
 * @returns {Promise<File>}
 */
export function cropAvatarWithTransform(image, options) {
  const {
    offsetX = 0,
    offsetY = 0,
    zoom = 1,
    viewportSize = 280,
    fileName = "avatar",
  } = options || {};

  return new Promise((resolve, reject) => {
    try {
      const nw = image.naturalWidth;
      const nh = image.naturalHeight;
      if (!nw || !nh) {
        reject(new Error("Imagem inválida"));
        return;
      }

      const scale = getAvatarCoverScale(nw, nh, viewportSize) * zoom;
      const displayedW = nw * scale;
      const displayedH = nh * scale;
      const imgLeft = viewportSize / 2 - displayedW / 2 + offsetX;
      const imgTop = viewportSize / 2 - displayedH / 2 + offsetY;

      const outSide = Math.min(viewportSize, MAX_AVATAR_PX);
      const outScale = outSide / viewportSize;
      const canvas = document.createElement("canvas");
      canvas.width = outSide;
      canvas.height = outSide;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas não disponível"));
        return;
      }

      // Fundo branco nas áreas vazias quando o zoom fica abaixo do enquadramento
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, outSide, outSide);
      ctx.drawImage(
        image,
        0,
        0,
        nw,
        nh,
        imgLeft * outScale,
        imgTop * outScale,
        displayedW * outScale,
        displayedH * outScale,
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Falha ao processar imagem"));
            return;
          }
          const baseName = (fileName || "avatar").replace(/\.[^.]+$/, "");
          resolve(
            new File([blob], `${baseName}.jpg`, {
              type: "image/jpeg",
              lastModified: Date.now(),
            }),
          );
        },
        "image/jpeg",
        0.92,
      );
    } catch (err) {
      reject(err);
    }
  });
}
